import { useEffect, useEffectEvent, useRef } from 'react';

import { useAtom } from 'jotai';

import { EditPlanDraft, editPlanDraftAtom } from '../state';

import { TaskValues } from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/types';

interface EditPlanDraftState {
  showAddQuiz: boolean;
  quizSectionId: string;
  quizIsInSection: boolean;
  showAddTask: boolean;
  taskValues: TaskValues;
  showAddSection: boolean;
  addSectionText: string;
  estimatedMinutes: string;
}

interface UseEditPlanDraftOptions {
  planId: string | undefined;
  planEstimatedMinutes: string;
  state: EditPlanDraftState;
  onRestore: (draft: Partial<EditPlanDraftState>) => void;
}

/**
 * Handles save/restore of edit plan draft state (quiz modal, task form, section form, estimated minutes).
 * Automatically saves when forms are open or estimated minutes has been edited.
 * Clears draft when all forms close and no unsaved edits remain.
 */
export const useEditPlanDraft = ({
  planId,
  planEstimatedMinutes,
  state,
  onRestore,
}: UseEditPlanDraftOptions) => {
  const [storedDraft, setStoredDraft] = useAtom(editPlanDraftAtom);
  const hasRestoredRef = useRef(false);

  // Restore draft on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (!planId) return;

    if (storedDraft?.planId === planId) {
      const restored: Partial<EditPlanDraftState> = {};

      if (storedDraft.showAddQuiz) {
        restored.showAddQuiz = true;
        restored.quizSectionId = storedDraft.quizSectionId;
        restored.quizIsInSection = storedDraft.quizIsInSection;
      }
      if (storedDraft.showAddTask) {
        restored.showAddTask = true;
        restored.taskValues = storedDraft.taskValues;
      }
      if (storedDraft.showAddSection) {
        restored.showAddSection = true;
        restored.addSectionText = storedDraft.addSectionText;
      }
      if (storedDraft.estimatedMinutes) {
        restored.estimatedMinutes = storedDraft.estimatedMinutes;
      }

      onRestore(restored);
    }

    hasRestoredRef.current = true;
  }, [planId, storedDraft, onRestore]);

  const saveDraft = useEffectEvent(() => {
    if (!planId) return;
    if (!hasRestoredRef.current) return;

    const {
      showAddQuiz,
      quizSectionId,
      quizIsInSection,
      showAddTask,
      taskValues,
      showAddSection,
      addSectionText,
      estimatedMinutes,
    } = state;

    const hasEditedEstimatedMinutes =
      estimatedMinutes !== '' && estimatedMinutes !== planEstimatedMinutes;
    const hasOpenForms = showAddQuiz || showAddTask || showAddSection;

    if (!hasOpenForms && !hasEditedEstimatedMinutes) {
      setStoredDraft(null);
      return;
    }

    const draft: EditPlanDraft = {
      planId,
      showAddQuiz,
      quizSectionId,
      quizIsInSection,
      showAddTask,
      taskValues,
      showAddSection,
      addSectionText,
      estimatedMinutes: hasEditedEstimatedMinutes ? estimatedMinutes : '',
      savedAt: Date.now(),
    };

    setStoredDraft(draft);
  });

  // Save draft when state changes
  useEffect(() => {
    saveDraft();
  }, [
    planId,
    planEstimatedMinutes,
    state.showAddQuiz,
    state.quizSectionId,
    state.quizIsInSection,
    state.showAddTask,
    state.taskValues.task,
    state.taskValues.notes,
    state.taskValues.link,
    state.showAddSection,
    state.addSectionText,
    state.estimatedMinutes,
  ]);
};
